import { useEffect } from 'react'

import { UseMutationResult } from '@tanstack/react-query'
import { SubmitHandler, UseFormProps, UseFormReturn } from 'react-hook-form'

import { useFormWithSchema } from '@fortem-monorepo/react-spa'
import { Form } from '@fortem-monorepo/react-ui'

import { defaultValues } from '@/components/forms/RunRiskCalibrationForm/default-values'
import {
  useRunAllDefaultJobsMutation,
  useRunCalibrationMutation,
  useRunCustomRiskMutation,
  useRunCustomStrategiesMutation,
  useRunDefaultRiskMutation,
  useRunDerivedMetricsMutation,
  useRunVoldivAdjustmentsMutation,
} from '@/react-query/mutations/batch-executor'

import { type SchemaType, schema } from './RunRiskCalibrationForm.schema'

export interface RunRiskCalibrationFormProps extends Omit<UseFormProps<SchemaType>, 'resolver'> {
  children?: ((form: UseFormReturn<SchemaType>) => React.ReactNode) | React.ReactNode
  onSubmit?: SubmitHandler<SchemaType>
}

export const RunRiskCalibrationForm: React.FC<RunRiskCalibrationFormProps> = ({
  children,
  onSubmit,
  ...props
}) => {
  const form = useFormWithSchema({ mode: 'all', ...props }, schema)

  const values = form.watch()
  const { input_type } = values

  const runAllDefaultJobsMutation = useRunAllDefaultJobsMutation()
  const runCustomStrategiesMutation = useRunCustomStrategiesMutation()
  const runVoldivAdjustmentsMutation = useRunVoldivAdjustmentsMutation()
  const runCalibrationMutation = useRunCalibrationMutation()
  const runDefaultRiskMutation = useRunDefaultRiskMutation()
  const runCustomRiskMutation = useRunCustomRiskMutation()
  const runDerivedMetricsMutation = useRunDerivedMetricsMutation()

  const submit = form.handleSubmit(
    onSubmit ??
      (async (_data, _e) => {
        let mutation: UseMutationResult<any, Error, any, unknown> | undefined = undefined

        switch (_data.input_type) {
          case 'all_jobs':
            mutation = runAllDefaultJobsMutation
            break
          case 'derived_metrics':
            mutation = runDerivedMetricsMutation
            break
          case 'custom_strategies':
            mutation = runCustomStrategiesMutation
            break
          case 'vol_div_adjustments':
            mutation = runVoldivAdjustmentsMutation
            break
          case 'calibration':
            mutation = runCalibrationMutation
            break
          case 'default_risk':
            mutation = runDefaultRiskMutation
            break
          case 'custom_risk':
            mutation = runCustomRiskMutation
            break
        }

        await mutation?.mutateAsync({ ..._data })

        form.reset({
          // @ts-ignore
          input_type: _data.input_type,
          ...defaultValues[input_type],
        })
      }),
  )

  /**
   * Set defaults
   */
  useEffect(() => {
    form.reset({
      ...values,
      // @ts-ignore
      input_type,
      ...defaultValues[input_type],
    })
    form.trigger()
  }, [input_type])

  /**
   * Trigger validation on start
   */
  useEffect(() => {
    form.trigger()
  }, [])

  return (
    <Form {...form}>
      <form onSubmit={submit}>{typeof children === 'function' ? children(form) : children}</form>
    </Form>
  )
}
